// New session (NewSession board; the app's new_session_sheet.dart): who
// can join, the name a blank field gets, and what POST /session sends.
// Every session the web makes loops (SI-26): is_looping is always true.

export const ACCESS = [
  { value: "invite", label: "Invite only", footer: "Only people you invite can join." },
  { value: "link", label: "Link", footer: "Anyone with the link can join." },
  { value: "public", label: "Public", footer: "Anyone can find it and join." },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The name a blank field gets: "Oct 17, 4:54 PM" (MMM d, h:mm a), local.
export function defaultSessionName(now = new Date()) {
  const h = now.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${MONTHS[now.getMonth()]} ${now.getDate()}, ${h12}:${mm} ${h < 12 ? "AM" : "PM"}`;
}

// Six random base36 characters ("k3j9x2"), from the browser's crypto when
// it has one.
export function randomKeySuffix(random = null) {
  const pick = random || cryptoRandom();
  let out = "";
  for (let i = 0; i < 6; i++) out += Math.floor(pick() * 36).toString(36);
  return out;
}

function cryptoRandom() {
  const c = typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (c && typeof c.getRandomValues === "function") {
    return () => c.getRandomValues(new Uint32Array(1))[0] / 4294967296;
  }
  return Math.random;
}

// What a blank name becomes (S5). The key (session_name, permanent, and
// all a link session's URL needs) is the date and time plus six random
// characters, so it can't be guessed from the minute it was made; the
// date and time alone is its alt_name, which every list shows. A typed
// name is the key, as before, with no alt_name.
export function sessionNames(typed, { now = new Date(), random = null } = {}) {
  const name = String(typed || "").trim();
  if (name) return { key: name, altName: "" };
  const shown = defaultSessionName(now);
  return { key: `${shown} ${randomKeySuffix(random)}`, altName: shown };
}

// A staged file's name as a session name: "dusty-pad_88_E.wav" reads
// "Dusty Pad 88 E" (today's dialog).
export function nameFromFile(fileName) {
  if (!fileName) return "";
  const base = String(fileName).replace(/\.[^/.]+$/, "");
  const spaced = base.replace(/[-_.]+/g, " ").trim();
  return spaced
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
    .slice(0, 80);
}

export function accessFooter(value) {
  const a = ACCESS.find((x) => x.value === value);
  return a ? a.footer : "";
}

// The body of POST /session (decision 4: privacy is saved; Invite only and
// Link are both private). `altName` rides along when set (a blank name's
// date and time; the create route takes an optional alt_name).
export function createSessionBody(name, access = "invite", { altName = "" } = {}) {
  const privacy = ACCESS.some((a) => a.value === access) ? access : "invite";
  const body = {
    session_name: String(name || "").trim(),
    private_session: privacy !== "public",
    is_looping: true,
    privacy,
  };
  const alt = String(altName || "").trim();
  if (alt) body.alt_name = alt;
  return body;
}

// A list row for Mine from the session POST /session made (the doc, or
// the name alone when the server sent nothing back).
export function rowFromCreated(doc, body, me) {
  const base = doc && typeof doc === "object" && doc.name ? doc : {};
  return {
    name: body.session_name,
    alt_name: body.alt_name || "",
    created_at: new Date().toISOString().replace("Z", ""),
    private: body.private_session,
    privacy: body.privacy,
    creator: me,
    users: me ? [me] : [],
    users_online: {},
    library: [],
    library_count: 0,
    favorited_by: [],
    is_looping: true,
    ...base,
  };
}
